import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MaterialModule } from '../../../material/material.module';
import { Book, Subject } from '../../../middle-school-math/models';

export interface BookDialogData {
  book?: Book;
  subjects: Subject[];
  defaultSubjectId?: string;
  isNew: boolean;
}

@Component({
  selector: 'app-admin-book-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, MaterialModule, MatSlideToggleModule],
  template: `
    <div class="dialog-container">
      <div class="dialog-header">
        <div class="title-wrap">
          <div class="book-icon-preview">
            <mat-icon>{{ book.icon || 'auto_stories' }}</mat-icon>
          </div>
          <div>
            <h2 mat-dialog-title class="dialog-title">{{ data.isNew ? 'Add New Book' : 'Edit Book Details' }}</h2>
            <p class="dialog-subtitle">{{ book.title || 'New Textbook / Course' }}</p>
          </div>
        </div>
        <button mat-icon-button (click)="cancel()" aria-label="Close dialog">
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <mat-divider></mat-divider>

      <div mat-dialog-content class="dialog-body">
        <div class="form-grid">
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Subject Category</mat-label>
            <mat-select [(ngModel)]="book.subjectId" required>
              <mat-option *ngFor="let s of data.subjects" [value]="s.id">
                <span [style.color]="s.color">●</span> {{ s.name }} ({{ s.id }})
              </mat-option>
            </mat-select>
            <mat-hint>Assign this book to a top-level subject</mat-hint>
          </mat-form-field>

          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Book Title</mat-label>
            <input matInput [(ngModel)]="book.title" (ngModelChange)="onTitleChange()" placeholder="e.g. Basics of Physics, Middle School Math" required />
            <mat-hint>The textbook or course name</mat-hint>
          </mat-form-field>

          <div class="field-with-btn full-width">
            <mat-form-field appearance="outline" class="flex-1">
              <mat-label>Book ID / Slug</mat-label>
              <input matInput [(ngModel)]="book.id" placeholder="e.g. basics_of_physics" required [disabled]="!data.isNew && !allowIdEdit" />
              <mat-hint>Unique URL-safe identifier for routing</mat-hint>
            </mat-form-field>
            <button mat-stroked-button color="primary" type="button" (click)="autoGenerateSlug()" *ngIf="data.isNew || allowIdEdit">
              <mat-icon>auto_fix_high</mat-icon> Generate
            </button>
            <button mat-icon-button type="button" (click)="allowIdEdit = !allowIdEdit" *ngIf="!data.isNew" matTooltip="Unlock ID edit">
              <mat-icon>{{ allowIdEdit ? 'lock_open' : 'lock' }}</mat-icon>
            </button>
          </div>

          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Description</mat-label>
            <textarea matInput [(ngModel)]="book.description" placeholder="Brief overview of curriculum covered in this book" rows="2"></textarea>
          </mat-form-field>

          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Chapters Data File Name</mat-label>
            <input matInput [(ngModel)]="book.chaptersFile" placeholder="e.g. basics_of_physics_chapters.json" />
            <mat-hint>JSON file stored in assets/data/</mat-hint>
          </mat-form-field>

          <!-- SUBSCRIPTION SETTINGS SECTION -->
          <div class="subscription-config-card">
            <div class="sub-header-row">
              <div class="sub-title-wrap">
                <mat-icon class="sub-icon">card_membership</mat-icon>
                <div>
                  <div class="sub-card-title">Paid Subscription & Paywall</div>
                  <div class="sub-card-subtitle">Require users to have an active subscription to access full chapters</div>
                </div>
              </div>
              <mat-slide-toggle [checked]="book.isSubscriptionEnabled" (change)="book.isSubscriptionEnabled = $event.checked" color="primary">
                {{ book.isSubscriptionEnabled ? 'Subscription Required' : 'Free Access' }}
              </mat-slide-toggle>
            </div>

            <div class="sub-pricing-row" *ngIf="book.isSubscriptionEnabled">
              <mat-form-field appearance="outline" class="flex-1">
                <mat-label>Monthly Price ($)</mat-label>
                <input matInput type="number" step="0.01" min="0.00" [(ngModel)]="book.monthlyPrice" placeholder="9.99" />
                <span matPrefix class="price-prefix">$ &nbsp;</span>
                <mat-hint>/ month recurring</mat-hint>
              </mat-form-field>

              <mat-form-field appearance="outline" class="flex-1">
                <mat-label>Annual Price ($)</mat-label>
                <input matInput type="number" step="0.01" min="0.00" [(ngModel)]="book.annualPrice" placeholder="79.99" />
                <span matPrefix class="price-prefix">$ &nbsp;</span>
                <mat-hint>/ year recurring</mat-hint>
              </mat-form-field>

              <mat-form-field appearance="outline" style="width: 110px;">
                <mat-label>Currency</mat-label>
                <input matInput [(ngModel)]="book.currency" placeholder="USD" />
              </mat-form-field>
            </div>

            <div class="sub-info-note" *ngIf="book.isSubscriptionEnabled">
              💡 <strong>Free Previews:</strong> You can mark individual chapters as <em>Free Preview</em> inside Chapter Configuration so non-subscribers can try them before purchasing.
            </div>
          </div>

          <!-- Icon Presets -->
          <div class="full-width selection-group">
            <label class="group-label">Select Book Icon</label>
            <div class="icon-presets">
              <button *ngFor="let ic of popularIcons" 
                      type="button" 
                      class="icon-btn" 
                      [class.active]="book.icon === ic" 
                      (click)="book.icon = ic">
                <mat-icon>{{ ic }}</mat-icon>
              </button>
            </div>
          </div>
        </div>
      </div>

      <mat-divider></mat-divider>

      <div mat-dialog-actions class="dialog-actions">
        <button mat-button type="button" (click)="cancel()">Cancel</button>
        <button mat-raised-button color="primary" type="button" (click)="save()">
          <mat-icon>save</mat-icon> {{ data.isNew ? 'Create Book' : 'Save Book' }}
        </button>
      </div>
    </div>
  `,
  styles: [`
    .dialog-container {
      display: flex;
      flex-direction: column;
      width: 100%;
      min-width: 540px;
    }
    .dialog-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 1.25rem 1.5rem;
      background: #fafafa;
    }
    .title-wrap {
      display: flex;
      align-items: center;
      gap: 1rem;
    }
    .book-icon-preview {
      height: 48px;
      width: 48px;
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      background: linear-gradient(135deg, #1e293b, #0f172a);
      box-shadow: 0 4px 10px rgba(0,0,0,0.15);
    }
    .book-icon-preview mat-icon {
      font-size: 28px;
      height: 28px;
      width: 28px;
      color: #93c5fd;
    }
    .dialog-title {
      margin: 0;
      font-size: 1.25rem;
      font-weight: 600;
    }
    .dialog-subtitle {
      margin: 0.2rem 0 0 0;
      font-size: 0.85rem;
      color: #666;
    }
    .dialog-body {
      padding: 1.25rem 1.5rem;
      max-height: 75vh;
      overflow-y: auto;
    }
    .form-grid {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
    }
    .full-width { width: 100%; }
    .field-with-btn {
      display: flex;
      gap: 0.5rem;
      align-items: flex-start;
    }
    .flex-1 { flex: 1; }

    .subscription-config-card {
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      border-radius: 12px;
      padding: 1.1rem 1.25rem;
      margin: 0.5rem 0;
    }
    .sub-header-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 1rem;
    }
    .sub-title-wrap {
      display: flex;
      align-items: center;
      gap: 0.75rem;
    }
    .sub-icon {
      color: #3b82f6;
      font-size: 26px;
      height: 26px;
      width: 26px;
    }
    .sub-card-title {
      font-weight: 700;
      font-size: 0.95rem;
      color: #1e293b;
    }
    .sub-card-subtitle {
      font-size: 0.8rem;
      color: #64748b;
    }
    .sub-pricing-row {
      display: flex;
      gap: 1rem;
      margin-top: 1rem;
    }
    .price-prefix {
      font-weight: 600;
      color: #475569;
    }
    .sub-info-note {
      font-size: 0.82rem;
      color: #475569;
      background: #eff6ff;
      border-left: 3px solid #3b82f6;
      padding: 8px 12px;
      border-radius: 4px;
      margin-top: 0.75rem;
    }

    .selection-group {
      margin-top: 0.5rem;
    }
    .group-label {
      display: block;
      font-size: 0.85rem;
      font-weight: 600;
      color: #475569;
      margin-bottom: 0.5rem;
    }
    .icon-presets {
      display: flex;
      gap: 0.5rem;
      flex-wrap: wrap;
    }
    .icon-btn {
      height: 40px;
      width: 40px;
      border-radius: 8px;
      border: 1px solid #e2e8f0;
      background: #f8fafc;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      color: #475569;
      transition: all 0.2s;
    }
    .icon-btn:hover {
      background: #e2e8f0;
    }
    .icon-btn.active {
      background: #1976d2;
      color: white;
      border-color: #1565c0;
    }
    .dialog-actions {
      display: flex;
      justify-content: flex-end;
      gap: 0.75rem;
      padding: 1rem 1.5rem;
      background: #fafafa;
    }
  `]
})
export class AdminBookDialogComponent implements OnInit {
  book: Book;
  allowIdEdit = false;

  popularIcons = ['school', 'auto_stories', 'speed', 'psychology', 'menu_book', 'science', 'calculate', 'biotech', 'laptop_chromebook', 'menu_book'];

  constructor(
    public dialogRef: MatDialogRef<AdminBookDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: BookDialogData
  ) {
    if (data.book) {
      this.book = JSON.parse(JSON.stringify(data.book));
      if (this.book.isSubscriptionEnabled === undefined) this.book.isSubscriptionEnabled = false;
      if (this.book.monthlyPrice === undefined) this.book.monthlyPrice = 9.99;
      if (this.book.annualPrice === undefined) this.book.annualPrice = 79.99;
      if (!this.book.currency) this.book.currency = 'USD';
    } else {
      this.book = {
        id: '',
        title: '',
        subjectId: data.defaultSubjectId || (data.subjects.length > 0 ? data.subjects[0].id : 'math'),
        description: '',
        icon: 'auto_stories',
        chaptersFile: '',
        displayOrder: 1,
        isSubscriptionEnabled: false,
        monthlyPrice: 9.99,
        annualPrice: 79.99,
        currency: 'USD'
      };
    }
  }

  ngOnInit() {}

  onTitleChange() {
    if (this.data.isNew && (!this.book.id || this.book.id.trim() === '')) {
      this.autoGenerateSlug();
    }
  }

  autoGenerateSlug() {
    if (this.book.title) {
      this.book.id = this.book.title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
      if (!this.book.chaptersFile) {
        this.book.chaptersFile = `${this.book.id}_chapters.json`;
      }
    }
  }

  save() {
    if (!this.book.title || this.book.title.trim() === '') {
      alert('Book Title is required');
      return;
    }
    if (!this.book.id || this.book.id.trim() === '') {
      alert('Book ID is required');
      return;
    }
    if (!this.book.subjectId || this.book.subjectId.trim() === '') {
      alert('Subject assignment is required');
      return;
    }
    if (!this.book.chaptersFile || this.book.chaptersFile.trim() === '') {
      this.book.chaptersFile = `${this.book.id}_chapters.json`;
    }
    this.dialogRef.close(this.book);
  }

  cancel() {
    this.dialogRef.close();
  }
}
